import React, { useCallback, useEffect, useRef } from 'react';

import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/useSharedPluginStateWithSelector';
import AkFlag from '@atlaskit/flag/flag';
import FlagGroup from '@atlaskit/flag/flag-group';
import CheckCircleIcon from '@atlaskit/icon/core/check-circle';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import { token } from '@atlaskit/tokens';

import { dismissDiagramPasteFlag } from '../../editor-commands/dismiss-diagram-paste-flag';
import type {
	MalleableUiPlugin,
	MalleableUiProductFlag,
	MalleableUiProductFlags,
} from '../../malleableUiPluginType';
import { MauiDiagramPasteFlagType } from '../../pm-plugins/maui-diagram-paste-plugin';

type Props = {
	api?: ExtractInjectionAPI<MalleableUiPlugin>;
	productFlags?: MalleableUiProductFlags;
};

const getIcon = (type: MauiDiagramPasteFlagType) => {
	switch (type) {
		case MauiDiagramPasteFlagType.SUCCESS:
			return <CheckCircleIcon label="" color={token('color.icon.success')} />;
		case MauiDiagramPasteFlagType.INFO:
			return <StatusInformationIcon label="" color={token('color.icon.information')} />;
		case MauiDiagramPasteFlagType.ERROR:
		default:
			return <StatusErrorIcon label="" color={token('color.icon.danger')} />;
	}
};

const getProductFlagType = (type: MauiDiagramPasteFlagType): MalleableUiProductFlag['type'] => {
	switch (type) {
		case MauiDiagramPasteFlagType.SUCCESS:
			return 'success';
		case MauiDiagramPasteFlagType.INFO:
			return 'info';
		case MauiDiagramPasteFlagType.ERROR:
		default:
			return 'error';
	}
};

export const MauiDiagramPasteFlag = ({ api, productFlags }: Props): React.JSX.Element | null => {
	const activeFlag = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.diagramPaste?.activeFlag ?? null,
	);
	const previousProductFlagIdRef = useRef<string | undefined>();

	const onDismissed = useCallback(() => {
		api?.core.actions.execute(dismissDiagramPasteFlag());
	}, [api]);

	useEffect(() => {
		if (!productFlags) {
			return;
		}

		const previousProductFlagId = previousProductFlagIdRef.current;
		if (!activeFlag) {
			if (previousProductFlagId) {
				productFlags.hideFlag(previousProductFlagId);
				previousProductFlagIdRef.current = undefined;
			}
			return;
		}

		if (previousProductFlagId && previousProductFlagId !== activeFlag.id) {
			productFlags.hideFlag(previousProductFlagId);
		}
		previousProductFlagIdRef.current = activeFlag.id;
		productFlags.showFlag({
			description: activeFlag.description ?? '',
			icon: getIcon(activeFlag.type),
			id: activeFlag.id,
			onDismissed,
			title: activeFlag.title ?? '',
			type: getProductFlagType(activeFlag.type),
		});
	}, [activeFlag, onDismissed, productFlags]);

	if (!activeFlag || productFlags) {
		return null;
	}

	const title = activeFlag.title ?? '';
	const description = activeFlag.description ?? '';
	const icon = getIcon(activeFlag.type);

	return (
		<FlagGroup>
			<AkFlag
				onDismissed={onDismissed}
				title={title}
				description={description}
				id={activeFlag.id}
				testId={activeFlag.id}
				icon={icon}
			/>
		</FlagGroup>
	);
};
